import { ReactNode, useMemo, useState } from 'react';
import * as SvgIcons from '@components/icons/svg-icon.tsx';
import { ICONS } from '@components/icons/icon.tsx';

// --- Source 1: svg-icon.tsx — one React component per named icon ---
const componentEntries = Object.entries(SvgIcons).filter(
  ([, value]) => typeof value === 'function'
) as [string, React.FC<any>][];

// --- Source 2: icon.tsx — ICONS map of name -> ready JSX node (menu icons) ---
const menuEntries = Object.entries(ICONS) as [string, ReactNode][];

// --- Source 3: assets/icons — all svg/png files, loaded as URLs via Vite glob ---
const assetModules = import.meta.glob<string>(
  '/src/assets/icons/**/*.{svg,png}',
  { eager: true, query: '?url', import: 'default' }
);
interface AssetIcon {
  name: string;
  folder: string;
  url: string;
}
const assetEntries: AssetIcon[] = Object.entries(assetModules)
  .map(([path, url]) => {
    const rel = path.replace('/src/assets/icons/', '');
    const parts = rel.split('/');
    const name = parts.pop() as string;
    const folder = parts.length ? parts.join('/') : 'root';
    return { name, folder, url };
  })
  .sort((a, b) => a.folder.localeCompare(b.folder) || a.name.localeCompare(b.name));

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

type Source = 'components' | 'menu' | 'assets';

const SOURCES: {
  key: Source;
  label: string;
  source: string;
  count: number;
}[] = [
  {
    key: 'components',
    label: 'Components',
    source: 'svg-icon.tsx',
    count: componentEntries.length
  },
  {
    key: 'menu',
    label: 'Menu',
    source: 'icon.tsx',
    count: menuEntries.length
  },
  {
    key: 'assets',
    label: 'Assets',
    source: 'assets/icons',
    count: assetEntries.length
  }
];

const Card = ({ name, children }: { name: string; children: ReactNode }) => (
  <div className="flex flex-col items-center justify-center gap-1.5 border border-gray-100 rounded-lg p-2 bg-white hover:shadow-sm transition-shadow min-h-[68px]">
    <div className="h-5 flex items-center justify-center text-gray-700 [&_svg]:max-h-5 [&_svg]:max-w-5">
      {children}
    </div>
    <span
      className="text-[9px] text-gray-400 text-center break-all leading-tight line-clamp-2"
      title={name}
    >
      {name}
    </span>
  </div>
);

const grid =
  'grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9 gap-2';

function IconsComponent() {
  const [source, setSource] = useState<Source>('components');
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const match = (name: string) => !q || name.toLowerCase().includes(q);

  // Alphabetical groups for named sources (components / menu).
  const letterGroups = useMemo(() => {
    const entries =
      source === 'components'
        ? componentEntries.filter(([n]) => match(n))
        : source === 'menu'
          ? menuEntries.filter(([n]) => match(n))
          : [];
    const map = new Map<string, [string, unknown][]>();
    entries.forEach((entry) => {
      const first = entry[0].charAt(0).toUpperCase();
      const key = /[A-Z]/.test(first) ? first : '#';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(entry as [string, unknown]);
    });
    return [...map.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([letter, icons]) => ({ letter, icons }));
  }, [source, q]);

  const availableLetters = useMemo(
    () => new Set(letterGroups.map((g) => g.letter)),
    [letterGroups]
  );

  // Folder groups for assets.
  const assetGroups = useMemo(() => {
    const filtered = assetEntries.filter((a) => match(a.name));
    const map = new Map<string, AssetIcon[]>();
    filtered.forEach((a) => {
      if (!map.has(a.folder)) map.set(a.folder, []);
      map.get(a.folder)!.push(a);
    });
    return [...map.entries()].map(([folder, icons]) => ({ folder, icons }));
  }, [q]);

  const total = SOURCES.find((s) => s.key === source)!.count;
  const isEmpty =
    source === 'assets'
      ? assetGroups.length === 0
      : letterGroups.length === 0;

  const jumpTo = (letter: string) => {
    const el = document.getElementById(`icon-letter-${letter}`);
    if (!el) return;
    let container: HTMLElement | null = el.parentElement;
    while (container) {
      const overflowY = getComputedStyle(container).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') break;
      container = container.parentElement;
    }
    if (!container) return;
    const JUMP_BAR_OFFSET = 96;
    const delta =
      el.getBoundingClientRect().top - container.getBoundingClientRect().top;
    container.scrollTo({
      top: container.scrollTop + delta - JUMP_BAR_OFFSET,
      behavior: 'smooth'
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-xl font-bold text-gray-800">
          Icons
          <span className="ml-2 text-sm font-normal text-gray-400">
            {total} total
          </span>
        </h2>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search icons…"
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-64 outline-none focus:border-[var(--primary)]"
        />
      </div>

      {/* Source switch */}
      <div className="flex gap-2">
        {SOURCES.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setSource(s.key)}
            className={`flex flex-col items-start px-4 py-2 rounded-lg transition-colors ${
              source === s.key
                ? 'bg-[var(--primary-50)] text-[var(--primary)]'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <span className="text-sm font-medium">
              {s.label}
              <span className="ml-1.5 text-xs opacity-60">{s.count}</span>
            </span>
            <span className="text-[10px] opacity-60">{s.source}</span>
          </button>
        ))}
      </div>

      {/* A–Z jump bar (named sources only) */}
      {source !== 'assets' && (
        <div className="sticky top-0 z-20 flex flex-wrap gap-1 bg-white/95 backdrop-blur py-2 border-b border-gray-100">
          {ALPHABET.map((letter) => {
            const enabled = availableLetters.has(letter);
            return (
              <button
                key={letter}
                type="button"
                disabled={!enabled}
                onClick={() => jumpTo(letter)}
                className={`w-6 h-6 rounded text-xs font-medium transition-colors ${
                  enabled
                    ? 'text-[var(--primary)] hover:bg-[var(--primary-50)]'
                    : 'text-gray-300 cursor-default'
                }`}
              >
                {letter}
              </button>
            );
          })}
        </div>
      )}

      {/* Named sources: alphabetical sections */}
      {source !== 'assets' &&
        letterGroups.map((group) => (
          <div key={group.letter} id={`icon-letter-${group.letter}`}>
            <p className="sticky top-11 z-10 bg-white py-1 text-sm font-bold text-gray-500">
              {group.letter}
            </p>
            <div className={`${grid} mt-2`}>
              {group.icons.map(([name, value]) => (
                <Card key={name} name={name}>
                  {source === 'components' ? (
                    (() => {
                      const Icon = value as React.FC<any>;
                      return <Icon />;
                    })()
                  ) : (
                    (value as ReactNode)
                  )}
                </Card>
              ))}
            </div>
          </div>
        ))}

      {/* Assets: folder sections */}
      {source === 'assets' &&
        assetGroups.map((group) => (
          <div key={group.folder}>
            <p className="py-1 text-sm font-bold text-gray-500">
              {group.folder}
              <span className="ml-2 text-xs font-normal text-gray-400">
                {group.icons.length}
              </span>
            </p>
            <div className={`${grid} mt-2`}>
              {group.icons.map((asset) => (
                <Card key={`${asset.folder}/${asset.name}`} name={asset.name}>
                  <img
                    src={asset.url}
                    alt={asset.name}
                    className="max-h-5 max-w-5 object-contain"
                  />
                </Card>
              ))}
            </div>
          </div>
        ))}

      {isEmpty && (
        <p className="text-gray-400 text-sm">No icons match “{query}”.</p>
      )}
    </div>
  );
}

export default IconsComponent;
